import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  TrendingUp,
  TrendingDown,
  AlertTriangle,
  CheckCircle,
  Clock,
  FileText,
  Users,
  Calendar,
  Shield,
  Target,
  Activity
} from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { useComplianceTasks, useDepartments } from "@/hooks";
import { calculateComplianceScore } from "@/utils/complianceScore";
import { ComplianceScoreCard } from "@/components/compliance/ComplianceScoreCard";
import { DepartmentHealthCard } from "@/components/compliance/DepartmentHealthCard";
import { InfoTooltip } from "@/components/ui/info-tooltip";
import { StatsCardSkeleton } from "@/components/ui/stats-card-skeleton";
import { Skeleton } from "@/components/ui/skeleton";
import { formatFramework, useCompliancePrograms, useProgramDetails } from "@/hooks/use-compliance-programs";

interface ComplianceMetrics {
  overallScore: number;
  totalPolicies: number;
  approvedPolicies: number;
  draftPolicies: number;
  policiesNeedingRenewal: number;
  avgApprovalTime: number;
  departmentMetrics: DepartmentMetric[];
  recentActivity: ActivityItem[];
  riskAlerts: RiskAlert[];
}

interface DepartmentMetric {
  department: string;
  policyCount: number;
  complianceRate: number;
  avgApprovalTime: number;
}

interface ActivityItem {
  id: string;
  type: 'policy_approved' | 'policy_rejected' | 'policy_created' | 'renewal_due';
  description: string;
  timestamp: string;
  policyTitle?: string;
  completedBy?: string;
}

interface RiskAlert {
  id: string;
  type: 'expiring' | 'overdue' | 'low_adoption' | 'workflow_bottleneck';
  severity: 'low' | 'medium' | 'high';
  title: string;
  description: string;
  policyId?: string;
  dueDate?: string;
}

interface CompletedTaskActivityRow {
  id: string;
  title: string;
  completed_at: string;
  completed_by: string | null;
}

interface ComplianceTaskActivityQuery extends PromiseLike<{ data: CompletedTaskActivityRow[] | null; error: unknown }> {
  select(columns: string): ComplianceTaskActivityQuery;
  eq(column: string, value: unknown): ComplianceTaskActivityQuery;
  not(column: string, operator: string, value: unknown): ComplianceTaskActivityQuery;
  order(column: string, options?: Record<string, unknown>): ComplianceTaskActivityQuery;
  limit(count: number): ComplianceTaskActivityQuery;
}

const complianceActivityDb = supabase as unknown as {
  from: (table: "compliance_tasks") => ComplianceTaskActivityQuery;
};

export default function Compliance() {
  const { profile } = useAuth();
  const [metrics, setMetrics] = useState<ComplianceMetrics | null>(null);
  const [loading, setLoading] = useState(true);
  const [selectedProgramId, setSelectedProgramId] = useState<string>("all");
  const { programs } = useCompliancePrograms();
  const selectedProgram = selectedProgramId === "all" ? null : programs.find((program) => program.id === selectedProgramId) ?? null;
  const { health: selectedProgramHealth } = useProgramDetails(selectedProgram?.id);
  
  // Fetch tasks for compliance scoring
  const { tasks } = useComplianceTasks();
  const { departments } = useDepartments();
  
  // Calculate compliance score from tasks
  const taskScore = tasks && departments ? calculateComplianceScore(tasks, departments) : null;

  useEffect(() => {
    if (profile?.company_id) {
      fetchComplianceMetrics();
    }
  }, [profile?.company_id]);

  const fetchComplianceMetrics = async () => {
    if (!profile?.company_id) return;

    try {
      setLoading(true);
      
      // Fetch all policies for the company
      const { data: policies, error: policiesError } = await supabase
        .from('policies')
        .select('*')
        .eq('company_id', profile.company_id);

      if (policiesError) throw policiesError;

      // Fetch workflow data for approval times
      const { data: workflows, error: workflowError } = await supabase
        .from('policy_workflow')
        .select('*')
        .in('policy_id', policies?.map(p => p.id) || []);

      if (workflowError) throw workflowError;

      // Calculate metrics
      const totalPolicies = policies?.length || 0;
      const approvedPolicies = policies?.filter(p => p.status === 'approved').length || 0;
      const draftPolicies = policies?.filter(p => p.status === 'draft').length || 0;
      
      // Calculate policies needing renewal (within next 90 days)
      const now = new Date();
      const renewalThreshold = new Date(now.getTime() + (90 * 24 * 60 * 60 * 1000));
      const policiesNeedingRenewal = policies?.filter(p => {
        if (!p.next_renewal_date) return false;
        const renewalDate = new Date(p.next_renewal_date);
        return renewalDate <= renewalThreshold;
      }).length || 0;

      // Calculate average approval time
      const approvalWorkflows = workflows?.filter(w => w.action === 'approved') || [];
      const avgApprovalTime = approvalWorkflows.length > 0 
        ? approvalWorkflows.reduce((sum, w) => {
            const created = new Date(w.created_at);
            // Use created_at for simplicity since we don't have updated_at in workflow
            return sum + ((new Date().getTime() - created.getTime()));
          }, 0) / approvalWorkflows.length / (1000 * 60 * 60 * 24) // Convert to days
        : 0;

      // Calculate overall compliance score
      const policyHealthScore = totalPolicies > 0 ? (approvedPolicies / totalPolicies) * 100 : 0;
      const renewalScore = totalPolicies > 0 ? Math.max(0, 100 - (policiesNeedingRenewal / totalPolicies) * 100) : 100;
      const overallScore = Math.round((policyHealthScore + renewalScore) / 2);

      // Mock department metrics (you can enhance this with actual department data)
      const departmentMetrics: DepartmentMetric[] = [
        { department: 'HR', policyCount: Math.floor(totalPolicies * 0.3), complianceRate: 85, avgApprovalTime: 5.2 },
        { department: 'IT', policyCount: Math.floor(totalPolicies * 0.25), complianceRate: 92, avgApprovalTime: 3.8 },
        { department: 'Finance', policyCount: Math.floor(totalPolicies * 0.2), complianceRate: 88, avgApprovalTime: 4.1 },
        { department: 'Operations', policyCount: Math.floor(totalPolicies * 0.25), complianceRate: 79, avgApprovalTime: 6.5 },
      ];

      // Fetch recent completed tasks for activity feed using direct SQL
      let recentActivity: ActivityItem[] = [];
      
      try {
        const { data, error } = await complianceActivityDb
          .from('compliance_tasks')
          .select('*')
          .eq('company_id', profile.company_id)
          .eq('status', 'complete')
          .not('completed_at', 'is', null)
          .order('completed_at', { ascending: false })
          .limit(10);

        if (!error && data && Array.isArray(data)) {
          // Get completed_by user IDs
          const userIds = data
            .map(task => task.completed_by)
            .filter(Boolean);
          
          // Fetch user names
          let userNames: Record<string, string> = {};
          if (userIds.length > 0) {
            const { data: profiles } = await supabase
              .from('profiles')
              .select('id, name')
              .in('id', userIds);
            
            if (profiles) {
              userNames = Object.fromEntries(profiles.map(p => [p.id, p.name]));
            }
          }

          recentActivity = data.map(task => ({
            id: task.id,
            type: 'policy_approved',
            description: `Task completed: ${task.title}`,
            timestamp: task.completed_at,
            policyTitle: task.title,
            completedBy: task.completed_by ? (userNames[task.completed_by] || 'Unknown') : 'Unknown'
          }));
        }
      } catch (error) {
        console.error('Error fetching recent tasks:', error);
      }

      // Generate risk alerts
      const riskAlerts: RiskAlert[] = [];
      
      // Check for expiring policies
      policies?.forEach(policy => {
        if (policy.next_renewal_date) {
          const renewalDate = new Date(policy.next_renewal_date);
          const daysUntilRenewal = Math.ceil((renewalDate.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));
          
          if (daysUntilRenewal <= 30 && daysUntilRenewal > 0) {
            riskAlerts.push({
              id: `expiring-${policy.id}`,
              type: 'expiring',
              severity: daysUntilRenewal <= 7 ? 'high' : daysUntilRenewal <= 14 ? 'medium' : 'low',
              title: 'Policy Renewal Due',
              description: `${policy.title} expires in ${daysUntilRenewal} days`,
              policyId: policy.id,
              dueDate: policy.next_renewal_date
            });
          }
        }
      });

      // Check for overdue policies
      const overduePolicies = policies?.filter(p => {
        if (!p.next_renewal_date) return false;
        return new Date(p.next_renewal_date) < now;
      }) || [];

      overduePolicies.forEach(policy => {
        riskAlerts.push({
          id: `overdue-${policy.id}`,
          type: 'overdue',
          severity: 'high',
          title: 'Overdue Policy Renewal',
          description: `${policy.title} renewal is overdue`,
          policyId: policy.id,
          dueDate: policy.next_renewal_date || undefined
        });
      });

      setMetrics({
        overallScore,
        totalPolicies,
        approvedPolicies,
        draftPolicies,
        policiesNeedingRenewal,
        avgApprovalTime,
        departmentMetrics,
        recentActivity,
        riskAlerts
      });

    } catch (error) {
      console.error('Error fetching compliance metrics:', error);
    } finally {
      setLoading(false);
    }
  };

  const getScoreColor = (score: number) => {
    if (score >= 90) return 'text-green-600';
    if (score >= 70) return 'text-yellow-600';
    return 'text-red-600';
  };

  const getScoreBgColor = (score: number) => {
    if (score >= 90) return 'bg-green-100';
    if (score >= 70) return 'bg-yellow-100';
    return 'bg-red-100';
  };

  const getAlertIcon = (type: string) => {
    switch (type) {
      case 'expiring': return <Clock className="h-4 w-4" />;
      case 'overdue': return <AlertTriangle className="h-4 w-4" />;
      case 'low_adoption': return <Users className="h-4 w-4" />;
      case 'workflow_bottleneck': return <Activity className="h-4 w-4" />;
      default: return <AlertTriangle className="h-4 w-4" />;
    }
  };

  const getSeverityColor = (severity: string) => {
    switch (severity) {
      case 'high': return 'border-red-200 bg-red-50 dark:border-red-900 dark:bg-red-950/30';
      case 'medium': return 'border-yellow-200 bg-yellow-50 dark:border-yellow-900 dark:bg-yellow-950/30';
      case 'low': return 'border-blue-200 bg-blue-50 dark:border-blue-900 dark:bg-blue-950/30';
      default: return 'border-gray-200 bg-gray-50 dark:border-gray-800 dark:bg-gray-900/40';
    }
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-medium text-foreground">Compliance Dashboard</h1>
            <p className="text-muted-foreground">Monitor your organization's policy compliance health</p>
          </div>
        </div>
        
        {/* Stats Cards Skeleton */}
        <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-4">
          {[...Array(4)].map((_, i) => (
            <StatsCardSkeleton key={i} />
          ))}
        </div>

        {/* Charts and Tables Skeleton */}
        <div className="grid gap-6 lg:grid-cols-2">
          <Card>
            <CardHeader>
              <Skeleton variant="shimmer" className="h-6 w-32" />
            </CardHeader>
            <CardContent>
              <Skeleton variant="shimmer" className="h-64 w-full" />
            </CardContent>
          </Card>
          <Card>
            <CardHeader>
              <Skeleton variant="shimmer" className="h-6 w-28" />
            </CardHeader>
            <CardContent>
              <Skeleton variant="shimmer" className="h-64 w-full" />
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  if (!metrics) {
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-medium text-foreground">Compliance Dashboard</h1>
            <p className="text-muted-foreground">Monitor your organization's policy compliance health</p>
          </div>
        </div>
        <Alert>
          <AlertTriangle className="h-4 w-4" />
          <AlertDescription>
            Unable to load compliance metrics. Please try again later.
          </AlertDescription>
        </Alert>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-medium tracking-tight">Compliance Dashboard</h1>
          <p className="text-muted-foreground">Monitor your organization's policy compliance health</p>
        </div>
        <div className="flex items-center gap-2">
          <Select value={selectedProgramId} onValueChange={setSelectedProgramId}>
            <SelectTrigger className="w-[240px]">
              <SelectValue placeholder="Program" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All programs</SelectItem>
              {programs.map((program) => (
                <SelectItem key={program.id} value={program.id}>{program.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button onClick={fetchComplianceMetrics} variant="outline">
            <Activity className="h-4 w-4 mr-2" />
            Refresh Metrics
          </Button>
        </div>
      </div>

      {selectedProgram && selectedProgramHealth && (
        <Card className="border-primary/20 bg-primary/[0.03]">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Shield className="h-5 w-5" />
              Program Health
              <Badge variant="outline">{formatFramework(selectedProgram.framework)}</Badge>
            </CardTitle>
            <CardDescription>{selectedProgram.name}</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid gap-5 lg:grid-cols-[1fr_360px] lg:items-center">
              <div className="space-y-3">
                <div className="flex items-center justify-between text-sm">
                  <span className="text-muted-foreground">Readiness</span>
                  <span className="font-medium">{selectedProgramHealth.readinessPercent}%</span>
                </div>
                <Progress value={selectedProgramHealth.readinessPercent} className="h-3" />
                <p className="text-sm text-muted-foreground">
                  Controls, evidence, and generated phase tasks for the selected program.
                </p>
              </div>
              <div className="grid grid-cols-3 gap-2 text-center text-sm">
                <div className="rounded-md bg-background p-3">
                  <p className="text-muted-foreground">Controls</p>
                  <p className="mt-1 text-xl font-semibold">{selectedProgramHealth.metControls}/{selectedProgramHealth.totalControls}</p>
                </div>
                <div className="rounded-md bg-background p-3">
                  <p className="text-muted-foreground">Evidence</p>
                  <p className="mt-1 text-xl font-semibold">{selectedProgramHealth.satisfiedEvidence}/{selectedProgramHealth.totalEvidence}</p>
                </div>
                <div className="rounded-md bg-background p-3">
                  <p className="text-muted-foreground">Open Tasks</p>
                  <p className="mt-1 text-xl font-semibold">{selectedProgramHealth.openTasks}</p>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Task-Based Compliance Score - Primary Health Indicator */}
      {taskScore && (
        <ComplianceScoreCard score={taskScore} />
      )}

      {/* Policy-Based Score (Secondary metric) */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Target className="h-5 w-5" />
            Policy Score
            <InfoTooltip content="Calculated from policy approval rates and renewal status. Higher scores indicate more approved policies and fewer renewals due. Formula: (Approved Policies / Total Policies × 50%) + (100 - Renewals Due / Total Policies × 50%)" />
          </CardTitle>
          <CardDescription>
            Based on policy approval rates, renewal status, and workflow efficiency
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex items-center gap-6">
            <div className={`rounded-full p-6 ${getScoreBgColor(metrics.overallScore)}`}>
              <div className={`text-4xl font-medium ${getScoreColor(metrics.overallScore)}`}>
                {metrics.overallScore}
              </div>
            </div>
            <div className="flex-1">
              <div className="mb-2">
                <Progress value={metrics.overallScore} className="h-3" />
              </div>
              <p className="text-sm text-muted-foreground">
                {metrics.overallScore >= 90 
                  ? "Excellent policyLet health" 
                  : metrics.overallScore >= 70 
                  ? "Good policy health with room for improvement"
                  : "Policy health needs attention"
                }
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Key Metrics */}
      <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-4">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3, delay: 0.1 }}
        >
          <Card>
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-2">
                    <p className="text-sm font-medium text-muted-foreground">Total Policies</p>
                    <InfoTooltip content="The total number of policies in your organization across all departments and statuses." />
                  </div>
                  <p className="text-2xl font-medium">{metrics.totalPolicies}</p>
                </div>
                <FileText className="h-8 w-8 text-muted-foreground" />
              </div>
            </CardContent>
          </Card>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3, delay: 0.15 }}
        >
          <Card>
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-2">
                    <p className="text-sm font-medium text-muted-foreground">Approved Policies</p>
                    <InfoTooltip content="Policies that have been approved and are currently active. This count excludes drafts, rejected, or pending approvals." />
                  </div>
                  <p className="text-2xl font-medium text-green-600">{metrics.approvedPolicies}</p>
                  <p className="text-xs text-muted-foreground">
                    {metrics.totalPolicies > 0 
                      ? `${Math.round((metrics.approvedPolicies / metrics.totalPolicies) * 100)}% of total`
                      : '0% of total'
                    }
                  </p>
                </div>
                <CheckCircle className="h-8 w-8 text-green-600" />
              </div>
            </CardContent>
          </Card>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3, delay: 0.2 }}
        >
          <Card>
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-2">
                    <p className="text-sm font-medium text-muted-foreground">Draft Policies</p>
                    <InfoTooltip content="Policies that are still in draft status and have not been submitted for review or approval." />
                  </div>
                  <p className="text-2xl font-medium text-yellow-600">{metrics.draftPolicies}</p>
                  <p className="text-xs text-muted-foreground">
                    {metrics.totalPolicies > 0 
                      ? `${Math.round((metrics.draftPolicies / metrics.totalPolicies) * 100)}% of total`
                      : '0% of total'
                    }
                  </p>
                </div>
                <Clock className="h-8 w-8 text-yellow-600" />
              </div>
            </CardContent>
          </Card>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3, delay: 0.25 }}
        >
          <Card>
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-2">
                    <p className="text-sm font-medium text-muted-foreground">Renewals Due</p>
                    <InfoTooltip content="Policies that are due for renewal within the next 90 days. These should be reviewed and updated to maintain compliance." />
                  </div>
                  <p className="text-2xl font-medium text-orange-600">{metrics.policiesNeedingRenewal}</p>
                  <p className="text-xs text-muted-foreground">Next 90 days</p>
                </div>
                <Calendar className="h-8 w-8 text-orange-600" />
              </div>
            </CardContent>
          </Card>
        </motion.div>
      </div>

      <div className="grid gap-6 lg:grid-cols-2">
        {/* Department Metrics */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Users className="h-5 w-5" />
              Department Compliance
            </CardTitle>
            <CardDescription>
              Compliance rates and approval times by department
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {metrics.departmentMetrics.map((dept, index) => (
                <div key={index} className="flex items-center justify-between p-3 border rounded-lg">
                  <div className="flex-1">
                    <div className="flex items-center justify-between mb-2">
                      <span className="font-medium">{dept.department}</span>
                      <span className="text-sm text-muted-foreground">
                        {dept.policyCount} policies
                      </span>
                    </div>
                    <div className="flex items-center gap-4">
                      <div className="flex-1">
                        <Progress value={dept.complianceRate} className="h-2" />
                        <p className="text-xs text-muted-foreground mt-1">
                          {dept.complianceRate}% compliance rate
                        </p>
                      </div>
                      <div className="text-right">
                        <p className="text-sm font-medium">{dept.avgApprovalTime.toFixed(1)}d</p>
                        <p className="text-xs text-muted-foreground">avg approval</p>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Risk Alerts */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5" />
              Risk Alerts
            </CardTitle>
            <CardDescription>
              Issues requiring immediate attention
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {metrics.riskAlerts.length === 0 ? (
                <div className="text-center py-6">
                  <CheckCircle className="h-12 w-12 text-green-600 mx-auto mb-2" />
                  <p className="text-sm text-muted-foreground">No active risk alerts</p>
                </div>
              ) : (
                metrics.riskAlerts.map((alert) => (
                  <div key={alert.id} className={`p-3 border rounded-lg ${getSeverityColor(alert.severity)}`}>
                    <div className="flex items-start gap-3">
                      {getAlertIcon(alert.type)}
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <p className="font-medium text-sm">{alert.title}</p>
                          <Badge variant={alert.severity === 'high' ? 'destructive' : alert.severity === 'medium' ? 'default' : 'secondary'}>
                            {alert.severity}
                          </Badge>
                        </div>
                        <p className="text-xs text-muted-foreground">{alert.description}</p>
                        {alert.dueDate && (
                          <p className="text-xs text-muted-foreground mt-1">
                            Due: {new Date(alert.dueDate).toLocaleDateString()}
                          </p>
                        )}
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Department Health */}
      {taskScore && taskScore.departmentScores.length > 0 && (
        <DepartmentHealthCard departmentScores={taskScore.departmentScores} />
      )}

      {/* Recent Activity */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Activity className="h-5 w-5" />
            Recent Compliance Activity
          </CardTitle>
          <CardDescription>
            Latest policy-related activities and updates
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {metrics.recentActivity.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">
                <CheckCircle className="h-12 w-12 mx-auto mb-2 text-muted-foreground/50" />
                <p>No recent task completions</p>
              </div>
            ) : (
              metrics.recentActivity.map((activity) => (
                <div key={activity.id} className="flex items-center gap-4 p-3 border rounded-lg hover:bg-accent/50">
                  <div className="flex-shrink-0">
                    <CheckCircle className="h-5 w-5 text-green-600" />
                  </div>
                  <div className="flex-1">
                    <p className="text-sm font-medium">{activity.description}</p>
                    <div className="flex items-center gap-2 mt-1">
                      {activity.completedBy && (
                        <>
                          <p className="text-xs text-muted-foreground">
                            Completed by <span className="font-medium">{activity.completedBy}</span>
                          </p>
                          <span className="text-xs text-muted-foreground">•</span>
                        </>
                      )}
                      <p className="text-xs text-muted-foreground">
                        {new Date(activity.timestamp).toLocaleDateString()} at {new Date(activity.timestamp).toLocaleTimeString()}
                      </p>
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
